import { CircleCheck, CircleDashed, TriangleAlert, WifiOff } from 'lucide-react';
import { cn } from './cn';

export type Status = 'healthy' | 'attention' | 'offline' | 'unknown';
export const STATUS_ORDER: Status[] = ['attention', 'offline', 'unknown', 'healthy'];

export const statusMeta: Record<Status, { label: string; icon: typeof CircleCheck; text: string; soft: string; hint: string }> = {
  healthy: { label: 'Healthy', icon: CircleCheck, text: 'text-healthy', soft: 'bg-healthy-soft', hint: 'Last reported healthy' },
  attention: { label: 'Attention', icon: TriangleAlert, text: 'text-attention', soft: 'bg-attention-soft', hint: 'Reported a condition that needs a look' },
  offline: { label: 'Offline', icon: WifiOff, text: 'text-offline', soft: 'bg-offline-soft', hint: 'Reported or detected as offline' },
  unknown: { label: 'Unknown', icon: CircleDashed, text: 'text-unknown', soft: 'bg-unknown-soft', hint: 'No status has been reported' },
};
export const toStatus = (value: string | null | undefined): Status =>
  value === 'healthy' || value === 'attention' || value === 'offline' ? value : 'unknown';

export function StatusBadge({ status, compact = false, className }: { status: string | null; compact?: boolean; className?: string }) {
  const s = toStatus(status), meta = statusMeta[s], Icon = meta.icon;
  return (
    <span title={meta.hint} className={cn('inline-flex items-center gap-1.5 rounded-full font-medium whitespace-nowrap',
      compact ? 'text-[12px]' : 'h-6 pl-1.5 pr-2.5 text-[12.5px]', !compact && meta.soft, meta.text,
      s === 'unknown' && !compact && 'border border-dashed border-unknown/50', className)}>
      <Icon aria-hidden className="size-3.5 shrink-0" strokeWidth={2.2} />
      {meta.label}
    </span>
  );
}

export const osLabel = (os: string | null) =>
  os === 'windows' ? 'Windows' : os === 'macos' ? 'macOS' : os === 'linux' ? 'Linux' : null;

export function OsText({ os }: { os: string | null }) {
  const label = osLabel(os);
  return label ? <span className="text-ink-2">{label}</span> : <span className="text-ink-3 italic" title="Operating system not reported">Unknown</span>;
}

/** Proportional bar of statuses. Unknown is hatched so it never reads as a known state. */
/** `quietOffline`: offline drawn as a plain status, not an alarm, in fleet summaries (WRMM-255). */
export function StatusBar({ counts, className, onSelect, quietOffline = false }: {
  counts: Record<Status, number>; className?: string; onSelect?: (status: Status) => void; quietOffline?: boolean;
}) {
  const total = STATUS_ORDER.reduce((sum, s) => sum + counts[s], 0);
  if (!total) return <div className={cn('h-2 rounded-full bg-sunken', className)} />;
  const fill: Record<Status, string> = { healthy: 'bg-healthy-fill', attention: 'bg-attention-fill', offline: quietOffline ? 'bg-line-strong' : 'bg-offline-fill', unknown: 'text-unknown/70 hatch bg-unknown-soft' };
  return (
    <div className={cn('flex h-2 gap-[2px] overflow-hidden rounded-full', className)} role="img"
      aria-label={STATUS_ORDER.filter(s => counts[s]).map(s => `${counts[s]} ${statusMeta[s].label}`).join(', ')}>
      {STATUS_ORDER.filter(s => counts[s]).map(s => (
        <span key={s} style={{ flexGrow: counts[s] }} onClick={onSelect ? () => onSelect(s) : undefined}
          className={cn('min-w-[3px] first:rounded-l-full last:rounded-r-full', fill[s], onSelect && 'cursor-pointer hover:opacity-80')} />
      ))}
    </div>
  );
}
